import {ConceptSelection, DisplayConcept, DisplayField, DisplayTerm, Language, ResultView} from '@twpub/core/models';
import {BaseConfigurableComponent} from '@twpub/shared/components/base';
import {Component, EventEmitter, Injector, Input, Output} from '@angular/core';
import {ConceptRelationService, SharedStateService} from '@twpub/core/services';
import {FeedbackModeService} from '@twpub/core/services/feedback-mode.service';
import {ComponentCategory} from '@twpub/core/enums';
import {ResultListProp} from '@twpub/core/constants';
import {formatDomainPaths} from '@twpub/shared/utils/domain-formatter';

@Component({
  template: '',
  standalone: false
})
export abstract class BaseResultViewComponent extends BaseConfigurableComponent implements ResultView {
  @Input() concept?: DisplayConcept;
  @Input() termId?: number;
  @Input() languages: Language[] = [];

  @Output() conceptSelected = new EventEmitter<ConceptSelection>()

  sourceTerm: DisplayTerm | undefined;
  sourceLangCodes?: string[];
  targetLangCodes?: string[];
  focusLangCode?: string;

  // Localized labels
  systemIdLabel = $localize`:@@baseResultView.systemId:System ID`;
  searchableInternalIdLabel = $localize`:@@baseResultView.searchableInternalId:Searchable internal ID`;
  conceptIdLabel = $localize`:@@baseResultView.conceptId:Concept ID`;
  domainLabel = $localize`:@@baseResultView.domain:Domain`;
  languageLabel = $localize`:@@baseResultView.language:Language`;
  dictionaryLabel = $localize`:@@baseResultView.dictionary:Dictionary`;
  sectionLabel = $localize`:@@baseResultView.section:Section`;

  protected sharedStateService: SharedStateService;
  protected relationService: ConceptRelationService;
  feedbackMode: FeedbackModeService;

  classificationFields: DisplayField[] = [];
  conceptFields: DisplayField[] = [];

  hasRelations: boolean = false;

  constructor(injector: Injector) {
    super();
    this.sharedStateService = injector.get(SharedStateService);
    this.relationService = injector.get(ConceptRelationService);
    this.feedbackMode = injector.get(FeedbackModeService);
  }

  override doUpdate(): void {
    super.doUpdate()
    if (this.concept) {
      this.relationService.getRelationsForConcept(this.concept.id).subscribe(relations => {
        this.hasRelations = relations?.length > 0;
      });
    }
    this.sourceTerm = this.getTerm(this.termId);
    this.sourceLangCodes = this.sourceTerm ? [this.sourceTerm.languageCode] : this.sharedStateService.getSourceLangs();
    this.targetLangCodes = this.sharedStateService.getTargetLangs();
    this.classificationFields = [];
    if (this.concept) {
      const cfFields = [this.getCidField(), this.getConceptIdField(), this.getDictionaryField(), this.getSectionField()];
      if (this.concept.domainPaths?.length) {
        cfFields.push(this.getDomainField());
      }
      this.classificationFields = this.concept ? cfFields : [];
    }
    this.conceptFields = this.concept?.fields || [];
  }

  protected getConceptIdField(): DisplayField {
    return {
      oid: 'id',
      jsonField: 'id',
      label: this.systemIdLabel,
      value: this.concept?.id.toString() || '',
      title: this.searchableInternalIdLabel
    }
  }

  protected getCidField(): DisplayField {
    return {oid: 'cid', jsonField: 'cid', label: this.conceptIdLabel, value: this.concept?.cid || ''}
  }

  protected getDictionaryField(): DisplayField {
    const dictField = this.concept?.dictionaryName;
    if (!dictField) {
      return {oid: '', jsonField: '', label: this.dictionaryLabel, value: ''};
    }
    return {
      ...dictField,
      label: this.dictionaryLabel
    };
  }

  protected getSectionField(): DisplayField {
    const sectionField = this.concept?.section;
    if (!sectionField) {
      return {oid: '', jsonField: '', label: this.sectionLabel, value: ''};
    }
    return {
      ...sectionField,
      label: this.sectionLabel
    };
  }

  protected getDomainField(): DisplayField {
    const paths = this.concept?.domainPaths;
    return {oid: 'DOM', jsonField: 'DOM', label: this.domainLabel, value: formatDomainPaths(paths)}
  }

  protected getTerm(termId?: number): DisplayTerm | undefined {
    return this.concept?.terms.find((t) => t.id === (termId || this.termId));
  }

  getLanguageField(langCode?: string): DisplayField {
    if (!this.languages?.length) throw new Error('No languages in component');
    const lang = this.languages.find((l) => l.code === (langCode || this.focusLangCode));
    return {oid: 'lang', jsonField: 'lang', label: this.languageLabel, value: lang?.name || ''}
  }

  getTerms(langCode: string) {
    return this.concept?.terms.filter((t) => t.languageCode === langCode)
  }

  getSynonymTerms(langCode?: string) {
    return this.concept?.terms.filter((t) => t.languageCode === langCode && t.id !== this.termId)
  }

  getTargetLanguages(): string[] {
    let pred: (t: DisplayTerm) => boolean;
    if (this.targetLangCodes?.length) {
      pred = (t) => this.isTargetLanguage(t.languageCode) && !this.isSourceLanguage(t.languageCode)
    } else {
      pred = (t) => !this.isSourceLanguage(t.languageCode);
    }
    const allCodes = this.concept?.terms.filter(pred).map((t) => t.languageCode) || [];
    return [...new Set(allCodes)];
  }

  private isSourceLanguage = (langCode: string) => !!this.sourceLangCodes?.some(source => source === langCode);
  private isTargetLanguage = (langCode: string) => !!this.targetLangCodes?.some(code => code === langCode);

  selectConcept(selection: ConceptSelection) {
    this.logger.trace('BaseResultViewComponent.selectConcept emitting:', {selection})
    const compConfig = this.sharedStateService.getComponentConfiguration(ComponentCategory.ResultList);
    selection.useIdSearch = compConfig?.getBoolean(ResultListProp.NeedSearchForEntry);
    this.conceptSelected.emit(selection);
  }

  selectDomains(domains: number[]) {
    this.sharedStateService.selectDomains(domains);
  }

  hasTermInLanguage(langCode: string | undefined) {
    return langCode && this.concept?.terms.some((t) => t.languageCode === langCode)
  }

  onConceptSectionClick(event: Event): void {
    if (!this.feedbackMode.isActive) return;
    event.stopPropagation();
    this.feedbackMode.openSectionFeedback({
      sectionType: 'concept',
      fields: [...this.classificationFields, ...this.conceptFields],
      concept: this.concept,
      term: this.getTerm(),
      languages: this.languages
    });
  }

  onTermClick(term: DisplayTerm, event: Event): void {
    if (!this.feedbackMode.isActive) return;
    event.stopPropagation();
    this.feedbackMode.openSectionFeedback({
      sectionType: 'term',
      fields: term.fields || [],
      concept: this.concept,
      term,
      languages: this.languages
    });
  }
}
